import React from 'react';
import { SearchDropdown, SearchItem } from '@/components/ui/search-dropdown';
import type { Product } from '@/types/firebase';

interface ProductSearchProps {
  products: Product[];
  value: string;
  onChange: (value: string) => void;
  onSelect: (product: Product) => void;
  onAddNew?: () => void;
  onCustomInput?: (value: string) => void; // For free text input
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  categoryId?: string;
  categoryName?: string; // For custom categories
}

export function ProductSearch({
  products,
  value,
  onChange,
  onSelect,
  onAddNew,
  onCustomInput,
  placeholder = 'Search products...',
  className = '',
  disabled = false,
  categoryId,
  categoryName
}: ProductSearchProps) {
  // Filter products by category if provided
  const filteredByCategory = categoryId 
    ? products.filter(product => product.category === categoryId)
    : products;

  // Convert products to SearchItem format
  const productItems: SearchItem[] = filteredByCategory.map(product => ({
    id: product.id,
    name: product.name,
    description: product.description,
    price: product.price,
    isTaxable: product.isTaxable,
    originalData: product
  }));

  // Custom filter function for products
  const filterProducts = (item: SearchItem, searchTerm: string) => {
    const term = searchTerm.toLowerCase();
    const product = item.originalData as Product;
    
    return (
      product.name.toLowerCase().includes(term) ||
      (product.description && product.description.toLowerCase().includes(term)) ||
      (product.sku && product.sku.toLowerCase().includes(term))
    );
  };

  return (
    <SearchDropdown
      items={productItems}
      value={value}
      onChange={onChange}
      onSelect={(item) => onSelect(item.originalData as Product)}
      onAddNew={onAddNew}
      onCustomInput={onCustomInput}
      addNewLabel="Add New Product"
      placeholder={placeholder}
      emptyMessage={!categoryId && !categoryName && products.length > 0 ? "Select a category first" : "No products found"}
      className={className}
      disabled={disabled}
      filterFunction={filterProducts}
    />
  );
}
